import { Accordion } from '@chakra-ui/react';
import { useAccordionControl } from '../../../../../hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DealRoomSettingWelcomeSchema,
  IDealRoomSettingWelcomeSchema,
} from '@monetize/types/app';
import { Controller, useForm } from 'react-hook-form';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MVStack,
} from '../../../../../components/Monetize';
import MEditor from '../../../../../components/Monetize/MEditor';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { SettingsSection } from '../../../components/SettingsSection';
import { SettingsToggle } from '../../../components/SettingsToggle';

interface DealRoomSettingWelcomeProps {
  value: IDealRoomSettingWelcomeSchema;
  onSubmit: (data: IDealRoomSettingWelcomeSchema) => Promise<void>;
  isLoading: boolean;
  isDisabled: boolean;
}

export const DealRoomSettingWelcome = ({
  value,
  onSubmit,
  isLoading,
  isDisabled,
}: DealRoomSettingWelcomeProps) => {
  const { accordionValue, handleAccordionChange } = useAccordionControl();
  const quoteSettingProcessingForm = useForm<IDealRoomSettingWelcomeSchema>({
    resolver: zodResolver(DealRoomSettingWelcomeSchema),
    mode: 'onChange',
    defaultValues: value,
    values: value,
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
    setValue,
  } = quoteSettingProcessingForm;

  const useDefaultWelcomeMessageWatch = watch('useDefaultWelcomeMessage');

  return (
    <Accordion.Root
      multiple
      w="full"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem value="0" label="Welcome Message">
        <MBox w="100%" pb={4}>
          <form>
            <MVStack alignItems="start" gap={4}>
              <SettingsSection showDivider={false}>
                <SettingsToggle
                  name="useDefaultWelcomeMessage"
                  heading="Use a Default Welcome Message"
                  subheading="Show a default welcome message in every Dealroom."
                  value={useDefaultWelcomeMessageWatch}
                  onChange={(val) => {
                    setValue('useDefaultWelcomeMessage', val, {
                      shouldDirty: true,
                    });
                  }}
                  isDisabled={isDisabled}
                />
              </SettingsSection>

              <SettingsSection showDivider={false}>
                <MFormField
                  error={errors.defaultWelcomeMessage}
                  label="Default Welcome Message"
                >
                  <Controller
                    name="defaultWelcomeMessage"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange, value, ...field } }) => (
                      <MEditor
                        autoScroll
                        handleEditorChange={onChange}
                        boxProps={{
                          pb: 0,
                          minH: 200,
                          w: '100%',
                        }}
                        value={value || ''}
                        {...field}
                        disabled={
                          !!isDisabled || !useDefaultWelcomeMessageWatch
                        }
                      />
                    )}
                  />
                </MFormField>
              </SettingsSection>
            </MVStack>
          </form>
        </MBox>

        <MFlex justifyContent="flex-end">
          <MButton
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            isDisabled={!isDirty || isDisabled}
            isLoading={isLoading}
          >
            Save
          </MButton>
        </MFlex>
      </MSettingAccordionItem>
    </Accordion.Root>
  );
};
